/**
 * BillingTab (Tab 7) — Broker billing overview.
 *
 * Card-based layout (no split panel). Shows current tier, monthly cost
 * breakdown, subscription status, and invoice history.
 *
 * Backend: GET /api/broker/billing/:brokerageId (existing).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign,
  faCreditCard,
  faFileInvoiceDollar,
  faCheckCircle,
  faCalendarAlt,
  faBuilding,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';

import axiosInstance from '../../../helpers/api';
import { colors, fonts } from '../shared';

interface BillingData {
  brokerage_id?: string;
  brokerage_name?: string;
  plan_type?: string;
  subscription_status?: string;
  current_month?: {
    agent_counts?: { total?: number; essential?: number; professional?: number; premium?: number };
    base_amount?: number;
    agent_amount?: number;
    total_amount?: number;
  };
  max_agents?: number;
  payment_method?: string | null;
  billing_history?: Array<{
    id?: string;
    invoice_number?: string;
    date?: string;
    amount?: number;
    status?: string;
    pdf_url?: string;
  }>;
  next_invoice_date?: string;
}

interface BillingTabProps {
  brokerageId?: string;
  data?: any;
}

const BillingTab: React.FC<BillingTabProps> = ({ brokerageId }) => {
  const [billing, setBilling] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // KOPS-465 — real Stripe invoices, fetched separately from the
  // billing summary because the summary endpoint doesn't hit Stripe.
  const [stripeInvoices, setStripeInvoices] = useState<NonNullable<BillingData['billing_history']>>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const fetchBilling = useCallback(async () => {
    if (!brokerageId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/broker/billing/${brokerageId}`);
      if (res.data?.success && res.data?.billing) {
        setBilling(res.data.billing);
      } else {
        setError(res.data?.error || 'Failed to load billing');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load billing');
    } finally {
      setLoading(false);
    }
  }, [brokerageId]);

  const fetchInvoices = useCallback(async () => {
    if (!brokerageId) return;
    setInvoicesLoading(true);
    try {
      const res = await axiosInstance.get(`/broker/billing/${brokerageId}/invoices`);
      if (res.data?.success && Array.isArray(res.data.invoices)) {
        // Map Stripe invoice shape to the InvoiceTable shape
        setStripeInvoices(
          res.data.invoices.map((inv: any) => ({
            id: inv.id,
            invoice_number: inv.number || inv.id?.slice(-8),
            date: inv.created,
            amount: inv.amount_paid || inv.amount_due || 0,
            status: inv.status,
            pdf_url: inv.invoice_pdf,
            hosted_url: inv.hosted_invoice_url,
            period_start: inv.period_start,
            period_end: inv.period_end,
          }))
        );
      }
    } catch {
      // Non-critical — silently fall back to empty
      setStripeInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, [brokerageId]);

  useEffect(() => {
    fetchBilling();
    fetchInvoices();
  }, [fetchBilling, fetchInvoices]);

  const current = billing?.current_month || {};
  const total = current.total_amount || 0;
  const base = current.base_amount || 0;
  const agents = current.agent_amount || 0;
  const agentCount = current.agent_counts?.total || 0;
  const isActive = !billing?.subscription_status || billing.subscription_status === 'active';

  return (
    <div style={{ flex: 1, background: colors.offBg, overflowY: 'auto' }}>
      <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        <HeaderCard loading={loading} error={error} billing={billing} />

        {/* Row 1 — 3 hero cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px',
            marginBottom: '20px',
          }}
        >
          <HeroCard
            icon={faBuilding}
            label="Current Tier"
            value={(billing?.plan_type || '—').toString().toUpperCase()}
            subValue={`Up to ${billing?.max_agents || '∞'} agents`}
            color={colors.gold}
          />
          <HeroCard
            icon={faDollarSign}
            label="Monthly Cost"
            value={`$${total.toLocaleString()}`}
            subValue={`Base $${base} + Agents $${agents}`}
            color={colors.green}
          />
          <HeroCard
            icon={faCalendarAlt}
            label="Next Invoice"
            value={billing?.next_invoice_date ? formatDate(billing.next_invoice_date) : '—'}
            subValue={`${agentCount} agent seat${agentCount === 1 ? '' : 's'}`}
            color={colors.blue}
          />
        </div>

        {/* Row 2 — Subscription status + Payment method */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '16px',
            marginBottom: '20px',
          }}
        >
          <SectionCard icon={faCheckCircle} title="Subscription Status">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                background: isActive ? `${colors.green}11` : `${colors.orange}11`,
                border: `1px solid ${isActive ? colors.green : colors.orange}55`,
                borderRadius: '8px',
              }}
            >
              <FontAwesomeIcon
                icon={faCheckCircle}
                style={{ color: isActive ? colors.green : colors.orange, fontSize: '24px' }}
              />
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: colors.navy }}>
                  {(billing?.subscription_status || 'active').toUpperCase()}
                </div>
                <div style={{ fontSize: '11px', color: colors.muted }}>
                  {isActive ? 'Payments current and up to date' : 'Requires attention'}
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={faCreditCard} title="Payment Method">
            {billing?.payment_method ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FontAwesomeIcon icon={faCreditCard} style={{ color: colors.gold, fontSize: '20px' }} />
                <div style={{ fontSize: '13px', color: colors.navy }}>{billing.payment_method}</div>
              </div>
            ) : (
              <div style={{ fontSize: '12px', color: colors.muted }}>No payment method on file</div>
            )}
          </SectionCard>
        </div>

        {/* Row 3 — Invoice history from Stripe */}
        <SectionCard icon={faFileInvoiceDollar} title="Invoice History">
          <InvoiceTable
            invoices={stripeInvoices.length > 0 ? stripeInvoices : billing?.billing_history || []}
            loading={invoicesLoading || loading}
          />
        </SectionCard>
      </div>
    </div>
  );
};

// ---- Sub-components --------------------------------------------------------

const HeaderCard: React.FC<{ loading: boolean; error: string | null; billing: BillingData | null }> = ({
  loading,
  error,
  billing,
}) => (
  <div
    style={{
      background: colors.card,
      borderLeft: `4px solid ${colors.gold}`,
      borderRadius: '8px',
      padding: '20px 24px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}
  >
    <h1
      style={{
        fontFamily: fonts.display,
        fontSize: '32px',
        color: colors.navy,
        margin: 0,
        letterSpacing: '0.5px',
      }}
    >
      Billing &amp; Subscription
    </h1>
    <p style={{ color: '#555', fontSize: '14px', margin: '4px 0 0 0' }}>
      {loading
        ? 'Loading billing data…'
        : error
        ? <span style={{ color: colors.red }}>{error}</span>
        : billing?.brokerage_name || 'Brokerage billing overview'}
    </p>
  </div>
);

const HeroCard: React.FC<{ icon: any; label: string; value: string; subValue?: string; color: string }> = ({
  icon,
  label,
  value,
  subValue,
  color,
}) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${color}`,
      borderRadius: '10px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
      <div
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '10px',
          background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 4px 10px ${color}33`,
        }}
      >
        <FontAwesomeIcon icon={icon} style={{ color: colors.white, fontSize: '18px' }} />
      </div>
    </div>
    <div style={{ fontFamily: fonts.display, fontSize: '28px', color: colors.navy, marginBottom: '4px', lineHeight: 1 }}>
      {value}
    </div>
    <div style={{ fontSize: '12px', fontWeight: 600, color: colors.navy }}>{label}</div>
    {subValue && <div style={{ fontSize: '11px', color: colors.muted, marginTop: '2px' }}>{subValue}</div>}
  </div>
);

const SectionCard: React.FC<{ icon: any; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${colors.gold}`,
      borderRadius: '10px',
      marginBottom: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}
  >
    <div
      style={{
        padding: '14px 20px',
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ color: colors.gold, fontSize: '14px' }} />
      <h5
        style={{
          margin: 0,
          fontSize: '11px',
          fontWeight: 700,
          color: colors.navy,
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        {title}
      </h5>
    </div>
    <div style={{ padding: '18px 20px' }}>{children}</div>
  </div>
);

const InvoiceTable: React.FC<{ invoices: NonNullable<BillingData['billing_history']>; loading: boolean }> = ({
  invoices,
  loading,
}) => {
  if (loading) return <div style={{ color: colors.muted, fontSize: '12px' }}>Loading…</div>;
  if (!invoices || invoices.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '24px', color: colors.muted }}>
        <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ fontSize: '28px', marginBottom: '8px' }} />
        <div style={{ fontSize: '13px' }}>No invoices yet</div>
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
            <Th>Invoice #</Th>
            <Th>Date</Th>
            <Th>Period</Th>
            <Th align="right">Amount</Th>
            <Th>Status</Th>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {invoices.slice(0, 24).map((inv: any, idx: number) => (
            <tr
              key={inv.id || idx}
              style={{
                borderBottom: `1px solid ${colors.border}`,
                background: idx % 2 === 0 ? 'transparent' : `${colors.off}44`,
              }}
            >
              <Td>{inv.invoice_number || `INV-${idx + 1}`}</Td>
              <Td>{inv.date ? formatDate(inv.date) : '—'}</Td>
              <Td>
                {inv.period_start && inv.period_end ? (
                  <span style={{ fontSize: '11px', color: colors.muted }}>
                    {formatDate(inv.period_start)} — {formatDate(inv.period_end)}
                  </span>
                ) : '—'}
              </Td>
              <Td align="right" style={{ fontWeight: 600 }}>
                ${Number(inv.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Td>
              <Td>
                <InvoiceStatusBadge status={inv.status} />
              </Td>
              <Td align="right">
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  {inv.pdf_url && (
                    <a
                      href={inv.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: colors.gold, fontWeight: 600, textDecoration: 'none', fontSize: '11px' }}
                    >
                      <FontAwesomeIcon icon={faDownload} /> PDF
                    </a>
                  )}
                  {inv.hosted_url && (
                    <a
                      href={inv.hosted_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: colors.blue, fontWeight: 600, textDecoration: 'none', fontSize: '11px' }}
                    >
                      View
                    </a>
                  )}
                  {!inv.pdf_url && !inv.hosted_url && (
                    <span style={{ color: colors.muted, fontSize: '11px' }}>—</span>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <th
    style={{
      padding: '8px 12px',
      textAlign: align,
      fontSize: '10px',
      fontWeight: 700,
      color: colors.muted,
      textTransform: 'uppercase',
      letterSpacing: '1px',
    }}
  >
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode; align?: 'left' | 'right'; style?: React.CSSProperties }> = ({ children, align = 'left', style }) => (
  <td style={{ padding: '10px 12px', textAlign: align, color: colors.navy, ...style }}>{children}</td>
);

const InvoiceStatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const s = (status || 'pending').toLowerCase();
  const color =
    s === 'paid' ? colors.green :
    s === 'pending' ? colors.orange :
    s === 'failed' || s === 'overdue' ? colors.red :
    colors.muted;
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {status || 'pending'}
    </span>
  );
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default BillingTab;
